/**
 * OrderTrackingPage.jsx
 *
 * Survives tab close:  reads from localStorage("lastOrder") if no location.state
 * Real-time status:   polls your API every 5s (or simulates if no endpoint)
 * Auto redirect:      goes to /menu after 10 minutes of inactivity on this page
 */

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ── Play success chime using Web Audio API ────────────────────────────────
// function playSuccessSound(orderNo) {
//   try {
//     // ✅ Step 1 — cancel any old speech
//     window.speechSynthesis.cancel();

//     // ✅ Step 2 — unlock AudioContext (helps on HTTP/Android)
//     try {
//       const ctx = new (window.AudioContext || window.webkitAudioContext)();
//       const buf = ctx.createBuffer(1, 1, 22050);
//       const src = ctx.createBufferSource();
//       src.buffer = buf;
//       src.connect(ctx.destination);
//       src.start(0);
//     } catch(_) {}

//     let spoken = false;

//     const doSpeak = () => {
//       if (spoken) return;
//       spoken = true;

//       setTimeout(() => {
//         // ✅ No second cancel here — removed

//         const utter = new SpeechSynthesisUtterance(
//           `Order placed successfully. Your token number is ${orderNo}`
//         );
//         utter.rate   = 0.88;
//         utter.pitch  = 1.2;
//         utter.volume = 1;
//         utter.lang   = "en-IN";

//         const voices = window.speechSynthesis.getVoices();

//         const femaleVoice =
//           voices.find(v => v.name === "Veena")                                            ||
//           voices.find(v => v.name === "Raveena")                                          ||
//           voices.find(v => v.name === "Samantha")                                         ||
//           voices.find(v => v.name === "Zira")                                             ||
//           voices.find(v => v.name.toLowerCase().includes("female"))                       ||
//           voices.find(v => v.lang === "en-IN")                                            ||
//           voices.find(v => v.lang === "en-US" && v.name.toLowerCase().includes("google")) ||
//           voices.find(v => v.lang === "en-US")                                            ||
//           null;

//         if (femaleVoice) utter.voice = femaleVoice;

//         // ✅ Android heartbeat fix
//         const heartbeat = setInterval(() => {
//           if (!window.speechSynthesis.speaking) {
//             clearInterval(heartbeat);
//             return;
//           }
//           window.speechSynthesis.pause();
//           window.speechSynthesis.resume();
//         }, 10000);

//         utter.onend   = () => clearInterval(heartbeat);
//         utter.onerror = () => clearInterval(heartbeat);

//         window.speechSynthesis.speak(utter);
//       }, 300);
//     };

//     const voices = window.speechSynthesis.getVoices();

//     if (voices.length > 0) {
//       doSpeak();
//     } else {
//       window.speechSynthesis.onvoiceschanged = () => {
//         window.speechSynthesis.onvoiceschanged = null;
//         doSpeak();
//       };
//     }

//   } catch(e) {
//     console.warn("Speech error:", e);
//   }
// }
function playSuccessSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();

    // ── Resume context (required for COD path — no prior user gesture on audio) ──
    const run = () => {
      // ── Reverb impulse ─────────────────────────────────────────────────
      const rvLen = ctx.sampleRate * 2.2;
      const rvBuf = ctx.createBuffer(2, rvLen, ctx.sampleRate);
      for (let c = 0; c < 2; c++) {
        const d = rvBuf.getChannelData(c);
        for (let i = 0; i < rvLen; i++)
          d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / rvLen, 2.8);
      }
      const rv = ctx.createConvolver();
      rv.buffer = rvBuf;

      // ── Compressor ─────────────────────────────────────────────────────
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.knee.value      = 10;
      comp.ratio.value     = 5;
      comp.attack.value    = 0.002;
      comp.release.value   = 0.20;
      comp.connect(ctx.destination);

      // ── Dry / Wet mix ──────────────────────────────────────────────────
      const dry = ctx.createGain(); dry.gain.value = 0.68;
      const wet = ctx.createGain(); wet.gain.value = 0.32;
      dry.connect(comp);
      rv.connect(wet);
      wet.connect(comp);

      // ── Stereo panner helper ───────────────────────────────────────────
      const pan = v => {
        const p = ctx.createStereoPanner
          ? ctx.createStereoPanner()
          : { pan: { value: 0 }, connect: n => n };   // fallback
        if (p.pan) p.pan.value = v;
        return p;
      };

      // ── Bell-note factory (sine + triangle + slight detune chorus) ─────
      const bell = (freq, t, dur, vol, panV = 0) => {
        [
          { type: "sine",     detune: 0,  mix: 1.00 },
          { type: "triangle", detune: 4,  mix: 0.30 },
          { type: "sine",     detune: -3, mix: 0.18 },  // sub-octave warmth
        ].forEach(({ type, detune, mix }) => {
          const osc = ctx.createOscillator();
          const g   = ctx.createGain();
          const p   = pan(panV);

          osc.type          = type;
          osc.frequency.value = type === "sine" && detune === -3 ? freq / 2 : freq;
          osc.detune.value  = detune;

          const t0 = ctx.currentTime + t;
          g.gain.setValueAtTime(0, t0);
          g.gain.linearRampToValueAtTime(vol * mix,     t0 + 0.010);
          g.gain.setTargetAtTime(vol * mix * 0.50,      t0 + 0.025, 0.055);
          g.gain.exponentialRampToValueAtTime(0.00001,  t0 + dur);

          osc.connect(g);
          g.connect(p);
          if (typeof p.connect === "function" && p !== g) {
            p.connect(dry);
            p.connect(rv);
          } else {
            g.connect(dry);
            g.connect(rv);
          }

          osc.start(t0);
          osc.stop(t0 + dur + 0.12);
        });
      };

      // ── Soft body thud (weight / impact) ──────────────────────────────
      const thud = ctx.createOscillator();
      const tG   = ctx.createGain();
      thud.type  = "sine";
      thud.frequency.setValueAtTime(160, ctx.currentTime);
      thud.frequency.exponentialRampToValueAtTime(38, ctx.currentTime + 0.14);
      tG.gain.setValueAtTime(0.55, ctx.currentTime);
      tG.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.20);
      thud.connect(tG); tG.connect(comp);
      thud.start(ctx.currentTime);
      thud.stop(ctx.currentTime + 0.24);

      // ── Noise burst (tactile click texture at t=0) ────────────────────
      const nBuf = ctx.createBuffer(1, ctx.sampleRate * 0.04, ctx.sampleRate);
      const nD   = nBuf.getChannelData(0);
      for (let i = 0; i < nD.length; i++) nD[i] = Math.random() * 2 - 1;
      const nSrc  = ctx.createBufferSource();
      const nFilt = ctx.createBiquadFilter();
      const nGain = ctx.createGain();
      nSrc.buffer       = nBuf;
      nFilt.type        = "bandpass";
      nFilt.frequency.value = 3200;
      nFilt.Q.value     = 0.8;
      nGain.gain.setValueAtTime(0.18, ctx.currentTime);
      nGain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.04);
      nSrc.connect(nFilt); nFilt.connect(nGain); nGain.connect(comp);
      nSrc.start(ctx.currentTime);

      // ── D-major arpeggio — bright, triumphant, premium ────────────────
      //    D4 → F#4 → A4 → D5 → F#5  (slight swing timing)
      bell(293.66, 0.00, 1.30, 0.52, -0.2);   // D4   root      — left
      bell(369.99, 0.14, 1.15, 0.46,  0.0);   // F#4  major 3rd — center
      bell(440.00, 0.27, 1.05, 0.42,  0.2);   // A4   fifth     — right
      bell(587.33, 0.40, 1.55, 0.38,  0.0);   // D5   octave    — center
      bell(739.99, 0.54, 1.80, 0.30,  0.0);   // F#5  shimmer   — center

      // ── Glitter tail: very high sine shimmer on final note ────────────
      const gl  = ctx.createOscillator();
      const glG = ctx.createGain();
      gl.type            = "sine";
      gl.frequency.value = 2349.32;                         // D7
      glG.gain.setValueAtTime(0, ctx.currentTime + 0.54);
      glG.gain.linearRampToValueAtTime(0.06,  ctx.currentTime + 0.58);
      glG.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 1.80);
      gl.connect(glG); glG.connect(dry); glG.connect(rv);
      gl.start(ctx.currentTime + 0.54);
      gl.stop(ctx.currentTime + 1.90);
    };

    // ── Handle suspended context (common on COD — no Razorpay interaction) ──
    if (ctx.state === "suspended") {
      ctx.resume().then(run).catch(() => {});
    } else {
      run();
    }

  } catch (e) {
    console.warn("Chime error:", e);
  }
}
const OT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
  @keyframes ot-spin  { to { transform:rotate(360deg); } }
  @keyframes ot-pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
  @keyframes ot-pop   { from{transform:scale(0.85);opacity:0} to{transform:scale(1);opacity:1} }
  @keyframes ot-rise  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ot-root { font-family:'DM Sans',system-ui,sans-serif; min-height:100vh; background:#f5f0eb; max-width:480px; margin:0 auto; display:flex; flex-direction:column; }
  .ot-card { background:#fff; border-radius:16px; padding:20px; border:0.5px solid #ede8e0; }
  .ot-step-active { animation:ot-pop 0.4s ease both; }
   @keyframes ot-confetti-fall {
    0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
  }
  @keyframes ot-scale-in {
    0%   { transform: scale(0) rotate(-10deg); opacity: 0; }
    60%  { transform: scale(1.15) rotate(3deg); }
    80%  { transform: scale(0.95) rotate(-1deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  }
  @keyframes ot-fade-up {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes ot-shimmer-slide {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes ot-ring-pulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(22,163,74,0.5); }
    50%     { box-shadow: 0 0 0 18px rgba(22,163,74,0); }
  }
  @keyframes ot-bounce-token {
    0%,100% { transform: translateY(0); }
    40%     { transform: translateY(-6px); }
    70%     { transform: translateY(-2px); }
  }
  @keyframes ot-bg-shift {
    0%,100% { background-position: 0% 50%; }
    50%     { background-position: 100% 50%; }
  }
    @media screen and (min-width: 601px) {
  .ot-root {
    max-width: min(1080px, 96vw) !important;
    width: 100% !important;
    margin: 0 auto !important;
  }
  .ot-card { padding: 22px !important; }
}
@media screen and (min-width: 601px) and (orientation: landscape) {
  .ot-root { max-width: min(1200px, 92vw) !important; }
}
`;
if (!document.getElementById("ot-css2")) {
  const el = document.createElement("style"); el.id = "ot-css2"; el.textContent = OT_CSS; document.head.appendChild(el);
}

const STEPS = [
  { id:1, icon:"📋", label:"Order placed",   desc:"We received your order" },
  { id:2, icon:"✅", label:"Confirmed",       desc:"Kitchen has accepted it" },
  { id:3, icon:"🍳", label:"Preparing",       desc:"Being freshly prepared" },
  { id:4, icon:"🎯", label:"Ready to collect", desc:"Come pick up your order!" },
];

const AUTO_REDIRECT_MINUTES = 10;

// ── Status badge ─────────────────────────────────────────────────────────────
function Badge({ text, color }) {
  const palette = {
    red:    { bg:"#fdecea", text:"#9b0d22", border:"#f5c6c6" },
    green:  { bg:"#e8f5e9", text:"#1b5e20", border:"#a5d6a7" },
    amber:  { bg:"#fff8e1", text:"#6d4c00", border:"#ffe082" },
    gray:   { bg:"#f5f5f5", text:"#555",    border:"#e0e0e0" },
  };
  const p = palette[color] || palette.gray;
  return (
    <span style={{ background:p.bg, color:p.text, border:`1px solid ${p.border}`,
      fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99,
      letterSpacing:0.3, whiteSpace:"nowrap" }}>{text}</span>
  );
}

export default function OrderTrackingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // ── Recover state from localStorage if tab was closed & reopened ──────────
  const stateFromNav = location.state;
  const stateFromStorage = (() => {
    try { return JSON.parse(localStorage.getItem("lastOrder") || "null"); } catch { return null; }
  })();
  const order = stateFromNav || stateFromStorage || {};

  const {
    invoiceNo  = "—",
    orderNo    = "—",
    total      = 0,
    discountedTotal,
  discount   = 0,      // ← ADD THIS
  discountAmt = 0,     // ← ADD THIS
    items      = [],
    orderType  = "Take Away",
    paymentType = "COD",
    paid       = false,
    placedAt   = Date.now(),
  } = order;
const displayTotal = Math.round(
  discountAmt > 0 ? (discountedTotal ?? total) : total
);
const isFirstLoad = useRef(!localStorage.getItem(`successShown_${orderNo}`));
const [showSuccess, setShowSuccess] = useState(isFirstLoad.current);
  const [step, setStep]         = useState(1);
  const [elapsed, setElapsed]   = useState(Math.floor((Date.now() - placedAt) / 1000));
  const [showItems, setShowItems] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_MINUTES * 60);
  const [showExpiry, setShowExpiry] = useState(false);
  const pollerRef  = useRef(null);
  const clockRef   = useRef(null);
  const countRef   = useRef(null);
  const isPaid = paid || paymentType === "Online";

  // ── Clock ─────────────────────────────────────────────────────────────────
// In the useEffect where you play sound and set timer, add the flag:
useEffect(() => {
  if (!orderNo || orderNo === "—") return;
  if (!isFirstLoad.current) return;
  localStorage.setItem(`successShown_${orderNo}`, "1");
  // sound removed from here — now plays in placeOrder() directly
  const timer = setTimeout(() => setShowSuccess(false), 4000);
  return () => clearTimeout(timer);
}, [orderNo]);

  // ── Auto-redirect countdown ───────────────────────────────────────────────
useEffect(() => {
  if (step === 4) return;
  countRef.current = setInterval(() => {
    setCountdown(c => {
      if (c <= 1) {
        clearInterval(countRef.current);
        //  Auto redirect to QR entry when time runs out
        localStorage.removeItem("lastOrder");
        localStorage.removeItem("cart");
         Object.keys(localStorage).forEach(key => {
  if (key.startsWith("successShown_")) {
    localStorage.removeItem(key);
  }
});
        navigate("/", { replace: true });
        return 0;
      }
      return c - 1;
    });
  }, 1000);
  return () => clearInterval(countRef.current);
}, [step]);

  // ── Status polling ────────────────────────────────────────────────────────
  // Replace the simulation block below with a real API call:
  //
  //   const pollStatus = async () => {
  //     try {
  //       const res = await fetch(`/api/order-status?invoice=${encodeURIComponent(invoiceNo)}`);
  //       const data = await res.json();
  //       // data.statusStep should be 1-4
  //       setStep(data.statusStep);
  //       if (data.statusStep >= 4) clearInterval(pollerRef.current);
  //     } catch (e) { console.warn("Poll failed:", e); }
  //   };
  //   pollerRef.current = setInterval(pollStatus, 5000);
  //   pollStatus();
  //
  // Simulation (remove once you have a real endpoint):
  useEffect(() => {
    const BREAKS = [12, 35, 60]; // seconds after placement to advance each step
    pollerRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 1;
        let ns = 1;
        let sum = 0;
        for (let i = 0; i < BREAKS.length; i++) {
          sum += BREAKS[i];
          if (next >= sum) ns = i + 2;
          else break;
        }
        setStep(Math.min(ns, 4));
        if (ns >= 4) clearInterval(pollerRef.current);
        return next;
      });
    }, 1000);
    return () => clearInterval(pollerRef.current);
  }, []);
  
//  Handle back button — navigate to QR entry page
// ── HARD LOCK: once a token is shown, back navigation is illegal ──
useEffect(() => {
  const hasValidOrder = order.orderNo && order.orderNo !== "—";
  if (!hasValidOrder) return; // nothing to protect yet

  window.history.pushState({ locked: true }, "", window.location.href);

  const handleBackButton = () => {
    // Always re-push — user is NEVER allowed back off this screen
    window.history.pushState({ locked: true }, "", window.location.href);
    // Optional: nudge them toward the explicit exit action instead
    setShowExpiry(true);
  };

  window.addEventListener("popstate", handleBackButton);

  const preventEdgeSwipe = (e) => {
    if (e.touches[0].clientX < 30) e.preventDefault();
  };
  document.addEventListener("touchstart", preventEdgeSwipe, { passive: false });

  return () => {
    window.removeEventListener("popstate", handleBackButton);
    document.removeEventListener("touchstart", preventEdgeSwipe);
  };
}, [order.orderNo]);
  const fmt = s => { const m = Math.floor(s/60), sec = s%60; return m > 0 ? `${m}m ${sec}s` : `${sec}s`; };
  const fmtCountdown = s => { const m = Math.floor(s/60), sec = String(s%60).padStart(2,"0"); return `${m}:${sec}`; };

//  const handleNewOrder = () => {
//   localStorage.removeItem("lastOrder");
//   localStorage.removeItem("cart");
//   localStorage.removeItem("selectedFreeItems");
//   localStorage.removeItem("allowedFreeQty");
//   Object.keys(localStorage).forEach(key => {
//     if (key.startsWith("successShown_")) localStorage.removeItem(key);
//   });
//   navigate("/", { replace: true });
// };
const handleNewOrder = () => {
  const minutesSincePlaced = (Date.now() - placedAt) / 60000;

  localStorage.removeItem("lastOrder");
  localStorage.removeItem("cart");
  localStorage.removeItem("selectedFreeItems");
  localStorage.removeItem("allowedFreeQty");
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith("successShown_")) localStorage.removeItem(key);
  });

  if (minutesSincePlaced < 5) {
    // Still within the order window — same table/store, let them order again quickly
    navigate("/menu", { replace: true });
  } else {
    // Session is stale — send them back to QR scan / home entry
    navigate("/", { replace: true });
  }
};
  if (!order.invoiceNo && !order.orderNo) {
    return (
        
      <div style={{ minHeight:"100vh", background:"#f5f0eb", display:"flex",
        flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, padding:24 }}>
        <div style={{ fontSize:40 }}>🔍</div>
        <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:16, color:"#4a4a4a", textAlign:"center" }}>
          No active order found.
        </p>
        <button onClick={() => navigate("/menu")} style={{ background:"#c8102e", color:"#fff",
          border:"none", borderRadius:12, padding:"13px 28px", fontSize:15, fontWeight:700, cursor:"pointer" }}>
          Browse Menu
        </button>
      </div>
    );
  }
// ✅ place this BEFORE final return

if (showSuccess) {
  // Confetti pieces config
  const confettiPieces = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    left: `${5 + (i * 5.5) % 92}%`,
    delay: `${(i * 0.15) % 2}s`,
    duration: `${2.2 + (i * 0.17) % 1.4}s`,
    color: ["#c8102e","#d4af37","#16a34a","#f97316","#3b82f6","#a855f7"][i % 6],
    size: `${8 + (i * 3) % 10}px`,
    shape: i % 3 === 0 ? "50%" : i % 3 === 1 ? "2px" : "0%",
  }));

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #1a0000 0%, #2d0509 40%, #1a0000 100%)",
      backgroundSize: "400% 400%",
      animation: "ot-bg-shift 8s ease infinite",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Sans', sans-serif",
      padding: "24px 20px",
      position: "relative",
      overflow: "hidden",
    }}>

      {/* Confetti */}
      {confettiPieces.map(p => (
        <div key={p.id} style={{
          position: "absolute",
          top: "-20px",
          left: p.left,
          width: p.size,
          height: p.size,
          borderRadius: p.shape,
          background: p.color,
          animation: `ot-confetti-fall ${p.duration} ${p.delay} ease-in forwards`,
          pointerEvents: "none",
          zIndex: 0,
        }} />
      ))}

      {/* Ambient glow */}
      <div style={{
        position: "absolute",
        width: 320,
        height: 320,
        borderRadius: "50%",
background: "radial-gradient(circle, rgba(200,16,46,0.35) 0%, rgba(100,0,10,0.15) 50%, transparent 70%)",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        pointerEvents: "none",
      }} />

      {/* Main card */}
      <div style={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: 380,
       background: "rgba(255,255,255,0.07)",
border: "1px solid rgba(255,100,100,0.2)",
        borderRadius: 28,
        padding: "36px 28px 32px",
        backdropFilter: "blur(20px)",
        textAlign: "center",
      }}>

        {/* Check circle */}
        <div style={{
          width: 88,
          height: 88,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #16a34a, #15803d)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 24px",
          animation: "ot-scale-in 0.6s cubic-bezier(0.34,1.56,0.64,1) both, ot-ring-pulse 2s 0.8s ease infinite",
          boxShadow: "0 8px 32px rgba(22,163,74,0.4)",
        }}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <path
              d="M10 20l7 7 13-13"
              stroke="#fff"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="100"
              strokeDashoffset="0"
              style={{ animation: "ot-draw-check 0.5s 0.4s ease both" }}
            />
          </svg>
        </div>

        {/* Title */}
        <h2 style={{
          margin: "0 0 6px",
          fontSize: 26,
          fontWeight: 800,
          background: "linear-gradient(135deg, #ffffff 0%, #ffb3b3 100%)",
WebkitBackgroundClip: "text",
WebkitTextFillColor: "transparent",
          letterSpacing: "-0.5px",
          animation: "ot-fade-up 0.5s 0.3s ease both",
          opacity: 0,
          animationFillMode: "forwards",
        }}>
          Order Placed!
        </h2>

        <p style={{
          margin: "0 0 28px",
          fontSize: 14,
         color: "rgba(255,180,180,0.7)",
          animation: "ot-fade-up 0.5s 0.45s ease both",
          opacity: 0,
          animationFillMode: "forwards",
        }}>
          Your food is being prepared
        </p>

        {/* Token badge */}
        <div style={{
          background: "linear-gradient(135deg, #c8102e, #9b0d22)",
          borderRadius: 20,
          padding: "20px 24px",
          marginBottom: 20,
          animation: "ot-fade-up 0.5s 0.55s ease both, ot-bounce-token 2s 1.2s ease infinite",
          opacity: 0,
          animationFillMode: "forwards",
          boxShadow: "0 8px 24px rgba(200,16,46,0.4)",
        }}>
          <p style={{
            margin: "0 0 4px",
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 2.5,
            color: "rgba(255,255,255,0.6)",
          }}>
            Your Token Number
          </p>
          <p style={{
            margin: 0,
            fontSize: 52,
            fontWeight: 800,
            color: "#fff",
            letterSpacing: -2,
            lineHeight: 1,
            background: "linear-gradient(90deg, #fff 0%, #ffd6d6 50%, #fff 100%)",
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "ot-shimmer-slide 2s 1s linear infinite",
          }}>
            {orderNo}
          </p>
        </div>

        {/* Amount */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "rgba(200,16,46,0.12)",
border: "1px solid rgba(200,16,46,0.25)",
          borderRadius: 14,
          padding: "14px 18px",
          marginBottom: 12,
          animation: "ot-fade-up 0.5s 0.65s ease both",
          opacity: 0,
          animationFillMode: "forwards",
        
        }}>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>
            Total Paid
          </span>
          <span style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>
            ₹{parseFloat(displayTotal).toFixed(2)}
          </span>
        </div>

        {/* Payment status */}
        {/* <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "7px 16px",
          borderRadius: 99,
          background: isPaid ? "rgba(22,163,74,0.15)" : "rgba(234,179,8,0.15)",
          border: `1px solid ${isPaid ? "rgba(22,163,74,0.3)" : "rgba(234,179,8,0.3)"}`,
          animation: "ot-fade-up 0.5s 0.75s ease both",
          opacity: 0,
          animationFillMode: "forwards",
        }}>
          <div style={{
            width: 7, height: 7, borderRadius: "50%",
            background: isPaid ? "#16a34a" : "#eab308",
          }} />
          <span style={{
            fontSize: 12, fontWeight: 700,
            color: isPaid ? "#4ade80" : "#fde047",
          }}>
            {isPaid ? "Payment confirmed" : "Pay at counter"}
          </span>
        </div> */}

        {/* Redirect hint */}
        <p style={{
          marginTop: 24,
          marginBottom: 0,
          fontSize: 12,
          color: "rgba(255,150,150,0.4)",
          animation: "ot-fade-up 0.5s 0.9s ease both",
          opacity: 0,
          animationFillMode: "forwards",
        }}>
          Loading order tracker…
        </p>
      </div>
    </div>
  );
}
  return (
    
    <div className="ot-root">
    
      {/* Expiry overlay */}
      {showExpiry && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)",
          zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:"#fff", borderRadius:"24px 24px 0 0", padding:"28px 24px 36px",
            width:"100%", maxWidth:480, textAlign:"center" }}>
          
            <p style={{ fontSize:18, fontWeight:800, color:"#1a1a1a", marginBottom:6 }}>Session expiring</p>
            <p style={{ fontSize:13, color:"#888", marginBottom:22 }}>
              This page will redirect to home in a moment.
            </p>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => { setShowExpiry(false); setCountdown(AUTO_REDIRECT_MINUTES * 60); }}
                style={{ flex:1, padding:"13px", borderRadius:12, border:"1.5px solid #e8e0d8",
                  background:"#fff", fontSize:14, fontWeight:700, cursor:"pointer", color:"#1a1a1a" }}>
                Stay on page
              </button>
              <button onClick={handleNewOrder}
                style={{ flex:1, padding:"13px", borderRadius:12, border:"none",
                  background:"#c8102e", color:"#fff", fontSize:14, fontWeight:700, cursor:"pointer" }}>
                New Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ background:"#1a1a1a", height:60, display:"flex", alignItems:"center",
        justifyContent:"space-between", padding:"0 16px", position:"sticky", top:0, zIndex:10 }}>
        <span style={{ color:"#fff", fontSize:16, fontWeight:700 }}>Order Status</span>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <Badge text={orderType.toUpperCase()} color="red" />
          
        </div>
      </div>

      {/* Scroll body */}
      <div style={{ flex:1, overflowY:"auto", padding:"16px 16px 110px" }}>

        {/* Token card */}
        <div style={{ background:"#96c5151a", borderRadius:20, padding:"20px 20px 22px",
          marginBottom:14, position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"#c8102e" }} />

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <p style={{ fontSize:10, color:"#130c0c", textTransform:"uppercase", letterSpacing:1.5, margin:"0 0 3px" }}>Token</p>
              <p style={{ fontSize:46, fontWeight:800, color:"#130101", margin:0, letterSpacing:-2, lineHeight:1 }}>{orderNo}</p>
            </div>
            <div style={{ textAlign:"right" }}>
              <p style={{ fontSize:10, color:"#130c0c", textTransform:"uppercase", letterSpacing:1.5, margin:"0 0 3px" }}>Total</p>
              <p style={{ fontSize:28, fontWeight:800, color:"#c8102e", margin:0 }}>₹{parseFloat(displayTotal).toFixed(2)}</p>
            </div>
          </div>

          
        
        </div>



        {/* Items (collapsible) */}
        <div className="ot-card" style={{ marginBottom:14, overflow:"hidden", padding:0 }}>
          <button onClick={() => setShowItems(!showItems)}
            style={{ width:"100%", padding:"16px 20px", background:"transparent", border:"none",
              display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer" }}>
            <span style={{ fontSize:14, fontWeight:700, color:"#1a1a1a", fontFamily:"'DM Sans',system-ui,sans-serif" }}>
              Your items ({items.length})
            </span>
            <span style={{ fontSize:20, color:"#bbb", lineHeight:1,
              transform: showItems ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}>▾</span>
          </button>
          {showItems && (
            <div style={{ borderTop:"0.5px solid #f0ebe5", padding:"0 20px 16px" }}>
              {items.map((it, i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between",
                  alignItems:"center", padding:"10px 0",
                  borderBottom: i < items.length-1 ? "0.5px solid #f5f0eb" : "none" }}>
                  <div>
                    <p style={{ margin:0, fontSize:14, fontWeight:500, color:"#1a1a1a" }}>
                      {it.ProductName || it.product_Name}
                    </p>
                    <p style={{ margin:"2px 0 0", fontSize:12, color:"#aaa" }}>
                      Qty: {it.Quantity || it.quantity || 1}
                    </p>
                  </div>
                  <p style={{ margin:0, fontSize:14, fontWeight:700, color:"#c8102e" }}>
                    ₹{parseFloat((it.Price || it.sosPrice || 0) * (it.Quantity || it.quantity || 1)).toFixed(2)}
                  </p>
                </div>
              ))}
              <div style={{ borderTop:"0.5px solid #f0ebe5", paddingTop:10, marginTop:4,
                display:"flex", justifyContent:"space-between" }}>
                <span style={{ fontSize:14, fontWeight:700, color:"#1a1a1a" }}>Total</span>
                <span style={{ fontSize:14, fontWeight:800, color:"#c8102e" }}>₹{parseFloat(displayTotal).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

   

        {/* Receipt info */}
        <div className="ot-card" style={{ marginBottom:8 }}>
          <p style={{ fontSize:12, fontWeight:700, color:"#888", textTransform:"uppercase",
            letterSpacing:1, margin:"0 0 12px" }}>Receipt info</p>
         {[
  ["Invoice", invoiceNo],
  ["Order type", orderType],
  ["Payment", isPaid ? "Paid online" : "Pay at counter (COD)"],
  order.discountAmt > 0 && ["Discount", `-₹${parseFloat(order.discountAmt || 0).toFixed(2)} (${order.discount}% off)`],
  {/* order.discount > 0 && ["Discount", `-₹${parseFloat(order.discountAmt || 0).toFixed(2)} (${order.discount}% off)`], */}
  ["Placed", new Date(placedAt).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" })],
].filter(Boolean).map(([k,v]) => ( 
            <div key={k} style={{ display:"flex", justifyContent:"space-between",
              padding:"7px 0", borderBottom:"0.5px solid #f5f0eb" }}>
              <span style={{ fontSize:13, color:"#aaa" }}>{k}</span>
              <span style={{ fontSize:13, fontWeight:600, color:"#1a1a1a" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      
    </div>
  );
}